import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BookOpenIcon,
  AcademicCapIcon,
  CalendarIcon,
  BriefcaseIcon,
  IdentificationIcon,
  MapPinIcon,
  GlobeAltIcon,
  UserGroupIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BuildingOfficeIcon,
  PlusIcon,
  PrinterIcon,
  DocumentArrowDownIcon,
  DocumentIcon,
  CameraIcon,
  XMarkIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../api/client';
import { FireLoader } from '../components/Loader';
import {
  showSuccess,
  showError,
  showWarning,
  showInfo,
  showConfirm,
} from '../utils/alert';
import { useTeacherSocket } from '../hooks/useTeacherSocket';
import { useAuth } from '../context/AuthContext';

// Helper to get full image URL
const getImageUrl = (profilePicture) => {
  if (!profilePicture) return null;
  if (profilePicture.startsWith('http://') || profilePicture.startsWith('https://')) {
    return profilePicture;
  }
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://lavender-falcon-985378.hostingersite.com/api/v1';
  const baseUrl = apiBaseUrl.replace('/api/v1', '');
  if (profilePicture.startsWith('/uploads')) {
    return `${baseUrl}${profilePicture}`;
  }
  if (profilePicture.startsWith('uploads/')) {
    return `${baseUrl}/${profilePicture}`;
  }
  return `${baseUrl}/uploads/teachers/${profilePicture}`;
};

// Helper to get full name with middle name
const getFullName = (firstName, middleName, lastName) => {
  const parts = [];
  if (firstName) parts.push(firstName);
  if (middleName && middleName.trim()) parts.push(middleName.trim());
  if (lastName) parts.push(lastName);
  return parts.join(' ') || 'N/A';
};

// Resolve the different response shapes used by the subject APIs. Sequelize
// associations may be lowercase, capitalized, flattened, or nested in the
// TeacherSubject join record.
const resolveSubjectAssignment = (item, subjects = [], classes = []) => {
  const join = item?.TeacherSubject || item?.teacherSubject || item?.teacher_subject || {};
  const embeddedSubject = item?.subject || item?.Subject || join?.subject || join?.Subject;
  const embeddedClass = item?.class || item?.Class || join?.class || join?.Class;
  const isDirectSubject = Boolean(item?.name && Object.keys(join).length);

  const subjectId = Number(
    embeddedSubject?.id ?? item?.subject_id ?? item?.subjectId ??
    join?.subject_id ?? join?.subjectId ?? (isDirectSubject ? item.id : 0)
  ) || null;
  const classId = Number(
    embeddedClass?.id ?? item?.class_id ?? item?.classId ??
    join?.class_id ?? join?.classId
  ) || null;
  const subjectFromList = subjects.find((subject) => Number(subject.id) === subjectId);
  const classFromList = classes.find((classItem) => Number(classItem.id) === classId);

  return {
    subjectId,
    classId,
    subjectName: embeddedSubject?.name || item?.subject_name || item?.subjectName ||
      join?.subject_name || subjectFromList?.name || (isDirectSubject ? item.name : null) ||
      'Unknown Subject',
    className: embeddedClass?.name || item?.class_name || item?.className ||
      join?.class_name || classFromList?.name || 'Unknown Class',
  };
};

export default function TeacherDetails() {
  const { planAccess } = useAuth();
  const isStarterPlan =
    String(planAccess?.plan?.name || '').trim().toLowerCase() === 'starter';
  const { id } = useParams();
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isExportingWord, setIsExportingWord] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // State for subject assignments
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);
  const [classesList, setClassesList] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [fetchingAssignments, setFetchingAssignments] = useState(false);
  const [loadingDropdown, setLoadingDropdown] = useState(false);

  const fetchTeacherDetailsRef = useRef();
  const fetchAssignedSubjectsRef = useRef();

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent.toLowerCase()));
    };
    checkMobile();
  }, []);

  const fetchTeacherDetails = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const teacherRes = await apiClient.get(`/teachers/${id}`);
      const teacherData = teacherRes.data.data || teacherRes.data;
      setTeacher(teacherData);

      const url = getImageUrl(teacherData.profile_picture);
      setImageUrl(url);
      setImageError(false);

      try {
        const statsRes = await apiClient.get(`/teachers/${id}/statistics`);
        let statsData = statsRes.data.data || statsRes.data;
        if (!statsData || typeof statsData !== 'object' || Object.keys(statsData).length === 0) {
          statsData = {
            total_students: 0,
            years_of_experience: teacherData.years_of_experience || 0,
            classes_taught: teacherData.classes || [],
            current_class_students: 0,
          };
        } else {
          if (!statsData.classes_taught || !Array.isArray(statsData.classes_taught)) {
            statsData.classes_taught = teacherData.classes || [];
          }
          if (!statsData.years_of_experience) {
            statsData.years_of_experience = teacherData.years_of_experience || 0;
          }
        }
        setStatistics(statsData);
      } catch (statsError) {
        console.error('Error fetching statistics, using fallback:', statsError);
        setStatistics({
          total_students: 0,
          years_of_experience: teacherData.years_of_experience || 0,
          classes_taught: teacherData.classes || [],
          current_class_students: 0,
        });
      }
    } catch (error) {
      await showError({ text: 'Failed to fetch teacher details' });
      console.error('Error fetching teacher details:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  const fetchAssignedSubjects = useCallback(async () => {
    try {
      setFetchingAssignments(true);
      const res = await apiClient.get(`/teachers/${id}/subjects`);
      const payload = res.data?.data ?? res.data;
      const data = Array.isArray(payload)
        ? payload
        : payload?.subjects || payload?.assignments || payload?.rows || [];
      setAssignedSubjects(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching assigned subjects:', error);
      await showError({ text: 'Failed to load subject assignments' });
    } finally {
      setFetchingAssignments(false);
    }
  }, [id]);

  const fetchDropdownData = useCallback(async () => {
    if (subjectsList.length > 0 && classesList.length > 0) return;
    setLoadingDropdown(true);
    try {
      const [subjectsRes, classesRes] = await Promise.all([
        apiClient.get('/subjects'),
        apiClient.get('/classes'),
      ]);
      const subjectsPayload = subjectsRes.data?.data ?? subjectsRes.data;
      const classesPayload = classesRes.data?.data ?? classesRes.data;
      const subjects = Array.isArray(subjectsPayload)
        ? subjectsPayload
        : subjectsPayload?.subjects || subjectsPayload?.rows || [];
      const classes = Array.isArray(classesPayload)
        ? classesPayload
        : classesPayload?.classes || classesPayload?.rows || [];
      setSubjectsList(Array.isArray(subjects) ? subjects : []);
      setClassesList(Array.isArray(classes) ? classes : []);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      await showError({ text: 'Failed to load subjects/classes' });
    } finally {
      setLoadingDropdown(false);
    }
  }, [subjectsList, classesList]);

  useEffect(() => {
    fetchDropdownData();
  }, [fetchDropdownData]);

  const refreshTeacherData = useCallback(async () => {
    await fetchTeacherDetails(true);
    await fetchAssignedSubjects();
  }, [fetchTeacherDetails, fetchAssignedSubjects]);

  useEffect(() => {
    fetchTeacherDetailsRef.current = fetchTeacherDetails;
    fetchAssignedSubjectsRef.current = fetchAssignedSubjects;
  }, [fetchTeacherDetails, fetchAssignedSubjects]);

  useEffect(() => {
    fetchTeacherDetails();
    fetchAssignedSubjects();
  }, [id, fetchTeacherDetails, fetchAssignedSubjects]);

  useTeacherSocket(async (event) => {
    const teacherId = parseInt(id);
    console.log('📡 Teacher update received:', event);
    if (event.action === 'deleted' && event.teacherId === teacherId) {
      await showWarning({ text: 'This teacher has been deleted by another user.' });
      navigate('/app/teachers');
      return;
    }
    if (event.teacherId === teacherId || event.action === 'updated' || event.action === 'created') {
      refreshTeacherData();
    }
  });

  const handleDelete = async () => {
    const confirmed = await showConfirm({
      title: 'Are you sure?',
      text: 'This action will permanently delete this teacher and all associated data. This cannot be undone.',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      icon: 'warning',
    });
    if (!confirmed) return;
    try {
      await apiClient.delete(`/teachers/${id}`);
      await showSuccess({ text: 'Teacher deleted successfully' });
      navigate('/app/teachers');
    } catch (error) {
      await showError({ text: 'Failed to delete teacher' });
      console.error('Error deleting teacher:', error);
    }
  };

  const handleOpenAssignModal = () => {
    setSelectedSubjectId('');
    setSelectedClassId('');
    setShowAssignModal(true);
  };

  const handleAssignSubject = async () => {
    if (!selectedSubjectId || !selectedClassId) {
      await showWarning({ text: 'Please select both a subject and a class' });
      return;
    }
    setAssigning(true);
    try {
      await apiClient.post('/subjects/assign-teacher', {
        teacher_id: parseInt(id),
        subject_id: parseInt(selectedSubjectId),
        class_id: parseInt(selectedClassId),
      });
      await showSuccess({ text: 'Subject assigned successfully!' });
      setShowAssignModal(false);
      await fetchAssignedSubjects();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to assign subject';
      await showError({ text: msg });
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveAssignment = async (subjectId, classId) => {
    const confirmed = await showConfirm({
      title: 'Remove Assignment?',
      text: 'Are you sure you want to remove this subject assignment?',
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel',
    });
    if (!confirmed) return;
    try {
      await apiClient.delete('/subjects/assign-teacher', {
        params: {
          teacher_id: parseInt(id),
          subject_id: subjectId,
          class_id: classId,
        },
      });
      await showSuccess({ text: 'Assignment removed' });
      await fetchAssignedSubjects();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to remove assignment';
      await showError({ text: msg });
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      await showWarning({ text: 'Please upload a valid image file (JPG, PNG, GIF, WEBP)' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      await showWarning({ text: 'Image size must be less than 5MB' });
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('profilePicture', file);
      const response = await apiClient.post(
        `/teachers/${id}/upload-profile-picture`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data?.success !== false) {
        await showSuccess({ text: 'Profile picture updated successfully!' });
        setImageError(false);
        await fetchTeacherDetails();
      } else {
        await showError({ text: response.data?.message || 'Failed to upload profile picture' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      await showError({ text: error.response?.data?.message || 'Failed to upload profile picture' });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteProfilePicture = async () => {
    const confirmed = await showConfirm({
      title: 'Remove Profile Picture?',
      text: 'Are you sure you want to remove the profile picture?',
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel',
    });
    if (!confirmed) return;
    try {
      const response = await apiClient.delete(`/teachers/${id}/profile-picture`);
      if (response.data?.success !== false) {
        await showSuccess({ text: 'Profile picture removed successfully' });
        await fetchTeacherDetails();
      }
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      await showError({ text: 'Failed to remove profile picture' });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-700',
      inactive: 'bg-gray-100 text-gray-700',
      on_leave: 'bg-yellow-100 text-yellow-700',
      terminated: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Active',
      inactive: 'Inactive',
      on_leave: 'On Leave',
      terminated: 'Terminated'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    if (status === 'active') return CheckCircleIcon;
    if (status === 'on_leave') return ClockIcon;
    if (status === 'terminated') return XCircleIcon;
    return XCircleIcon;
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const generatePrintHTML = () => {
    const fullName = getFullName(teacher.first_name, teacher.middle_name, teacher.last_name);
    const statusLabel = getStatusLabel(teacher.status);
    const statusColorClass = teacher.status === 'active' ? 'status-active' :
                            teacher.status === 'on_leave' ? 'status-onleave' :
                            teacher.status === 'terminated' ? 'status-terminated' : 'status-inactive';

    let classesHtml = '';
    if (teacher.classes && teacher.classes.length > 0) {
      classesHtml = `<ul style="margin:0;padding-left:20px;">`;
      teacher.classes.forEach(cls => {
        const isCurrent = cls.id === teacher.current_class_id ? ' (Current)' : '';
        classesHtml += `<li>${cls.name} - ${cls.level} (${cls.faculty})${isCurrent}</li>`;
      });
      classesHtml += `</ul>`;
    } else {
      classesHtml = 'None assigned';
    }

    let subjectHtml = 'None assigned';
    if (assignedSubjects && assignedSubjects.length > 0) {
      subjectHtml = `<ul style="margin:0;padding-left:20px;">`;
      assignedSubjects.forEach(item => {
        const { subjectName, className } = resolveSubjectAssignment(
          item,
          subjectsList,
          classesList,
        );
        subjectHtml += `<li>${subjectName} - ${className}</li>`;
      });
      subjectHtml += `</ul>`;
    }

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Teacher Details - ${fullName}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Times New Roman', Times, serif;
              padding: 30px 40px;
              color: #1a1a1a;
              background: white;
              font-size: 12px;
            }
            @page {
              size: A4;
              margin: 15mm 20mm;
            }
            .print-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 3px solid #1a3a5c;
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            .print-header-left h1 {
              font-size: 20px;
              color: #1a3a5c;
              font-weight: bold;
              letter-spacing: 1px;
            }
            .print-header-left p {
              color: #555;
              font-size: 11px;
              margin-top: 3px;
            }
            .print-header-right {
              text-align: right;
              font-size: 10px;
              color: #666;
            }
            .print-profile {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 25px;
              padding: 15px;
              background: #f8f9fa;
              border: 1px solid #e0e0e0;
              border-radius: 4px;
            }
            .print-profile-info h2 {
              font-size: 18px;
              color: #1a3a5c;
              margin-bottom: 5px;
            }
            .print-profile-info .sub-info {
              color: #555;
              font-size: 12px;
            }
            .print-profile-info .sub-info span {
              display: inline-block;
              margin-right: 15px;
            }
            .print-profile-image {
              width: 100px;
              height: 120px;
              border: 2px solid #1a3a5c;
              border-radius: 4px;
              overflow: hidden;
              flex-shrink: 0;
              background: #f0f0f0;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .print-profile-image img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
            .print-profile-image .initials {
              font-size: 32px;
              font-weight: bold;
              color: #1a3a5c;
            }
            .status-badge {
              display: inline-block;
              padding: 2px 10px;
              border-radius: 3px;
              font-size: 10px;
              font-weight: 600;
              text-transform: uppercase;
            }
            .status-active { background: #d4edda; color: #155724; }
            .status-onleave { background: #fff3cd; color: #856404; }
            .status-terminated { background: #f8d7da; color: #721c24; }
            .status-inactive { background: #e2e3e5; color: #383d41; }

            .print-section {
              margin-bottom: 20px;
            }
            .print-section h3 {
              font-size: 13px;
              color: #1a3a5c;
              border-bottom: 2px solid #e0e0e0;
              padding-bottom: 5px;
              margin-bottom: 10px;
              text-transform: uppercase;
              letter-spacing: 1px;
              font-weight: bold;
            }
            .print-table {
              width: 100%;
              border-collapse: collapse;
            }
            .print-table tr {
              border-bottom: 1px solid #f0f0f0;
            }
            .print-table td {
              padding: 6px 8px;
              font-size: 11px;
              vertical-align: top;
            }
            .print-table td:first-child {
              width: 35%;
              font-weight: 600;
              color: #444;
              background: #fafafa;
              padding-left: 12px;
            }
            .print-table td:last-child {
              width: 65%;
              color: #1a1a1a;
            }
            .print-footer {
              text-align: center;
              margin-top: 25px;
              padding-top: 12px;
              border-top: 1px solid #e0e0e0;
              color: #888;
              font-size: 9px;
            }
            .print-footer p { margin-top: 2px; }
            .print-watermark {
              position: fixed;
              bottom: 20px;
              right: 20px;
              opacity: 0.1;
              font-size: 40px;
              color: #1a3a5c;
              font-weight: bold;
              transform: rotate(-20deg);
              pointer-events: none;
            }
            @media print {
              .no-print { display: none !important; }
              body { padding: 20px 30px; }
            }
          </style>
        </head>
        <body>
          <div class="print-watermark">PAWA SCHOOL</div>

          <div class="print-header">
            <div class="print-header-left">
              <h1>PAWASCHOOL</h1>
              <p>Teacher Information Report</p>
            </div>
            <div class="print-header-right">
              <p>Generated: ${new Date().toLocaleDateString()}</p>
              <p>Time: ${new Date().toLocaleTimeString()}</p>
            </div>
          </div>

          <div class="print-profile">
            <div class="print-profile-info">
              <h2>${fullName}</h2>
              <div class="sub-info">
                <span>Employee #: ${teacher.employee_number}</span>
                <span>|</span>
                <span>Status: ${statusLabel}</span>
                <span>|</span>
                <span>${teacher.qualification || 'N/A'}</span>
              </div>
              <div style="margin-top: 6px;">
                <span class="status-badge ${statusColorClass}">${statusLabel.toUpperCase()}</span>
                <span style="margin-left: 10px; font-size: 11px; color: #555;">Joined: ${formatDate(teacher.joining_date)}</span>
              </div>
            </div>
            <div class="print-profile-image">
              ${teacher.profile_picture && !imageError ?
                `<img src="${imageUrl}" alt="${fullName}" />` :
                `<div class="initials">${getInitials(teacher.first_name, teacher.last_name)}</div>`
              }
            </div>
          </div>

          <div class="print-section">
            <h3>Personal Information</h3>
            <table class="print-table">
              <tr><td>Full Name</td><td>${fullName}</td></tr>
              <tr><td>Employee Number</td><td>${teacher.employee_number}</td></tr>
              <tr><td>Date of Birth</td><td>${formatDate(teacher.date_of_birth)}</td></tr>
              <tr><td>Nationality</td><td>${teacher.nationality || 'Not set'}</td></tr>
              <tr><td>Address</td><td>${[teacher.address, teacher.city, teacher.state, teacher.country].filter(Boolean).join(', ') || 'Not set'}</td></tr>
              <tr><td>Phone</td><td>${teacher.phone || 'Not set'}</td></tr>
              <tr><td>Email</td><td>${teacher.email || 'Not set'}</td></tr>
            </table>
          </div>

          <div class="print-section">
            <h3>Professional Information</h3>
            <table class="print-table">
              <tr><td>Qualification</td><td>${teacher.qualification || 'N/A'}</td></tr>
              <tr><td>Teacher Status</td><td>${teacher.teacher_status || 'N/A'}</td></tr>
              <tr><td>PIN Code</td><td>${teacher.pin_code || 'N/A'}</td></tr>
              <tr><td>NIN Number</td><td>${teacher.nin_number || 'N/A'}</td></tr>
              <tr><td>Years of Experience</td><td>${teacher.years_of_experience || 0} years</td></tr>
              <tr><td>Joining Date</td><td>${formatDate(teacher.joining_date)}</td></tr>
              <tr><td>Current Class</td><td>${teacher.currentClass?.name || 'Not assigned'}</td></tr>
            </table>
          </div>

          <div class="print-section">
            <h3>Assigned Classes</h3>
            <div style="font-size: 11px; padding: 6px 8px;">
              ${classesHtml}
            </div>
          </div>

          <div class="print-section">
            <h3>Subject Assignments</h3>
            <div style="font-size: 11px; padding: 6px 8px;">
              ${subjectHtml}
            </div>
          </div>

          ${statistics ? `
          <div class="print-section">
            <h3>Statistics</h3>
            <table class="print-table">
              <tr><td>Total Students</td><td>${statistics.total_students || 0}</td></tr>
              <tr><td>Classes Taught</td><td>${statistics.classes_taught?.length || 0}</td></tr>
              <tr><td>Current Class Students</td><td>${statistics.current_class_students || 0}</td></tr>
            </table>
          </div>
          ` : ''}

          <div class="print-footer">
            <p>This is a computer-generated document. No signature required.</p>
            <p>PawaSchool Management System | All Rights Reserved</p>
          </div>
        </body>
      </html>
    `;
  };

  const handlePrint = async () => {
    if (isStarterPlan) {
      return showWarning({
        title: 'PDF Download Not Available',
        text: 'PDF downloads are not included in the Starter plan. Upgrade to Basic or a higher plan to download PDFs.',
      });
    }

    setIsPrinting(true);
    try {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (printWindow) {
        printWindow.document.write(generatePrintHTML());
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    } catch (error) {
      console.error('Print error:', error);
      await showError({ text: 'Failed to generate print preview' });
    } finally {
      setIsPrinting(false);
      setShowPrintMenu(false);
    }
  };

  const handleExportWord = async () => {
    setIsExportingWord(true);
    const fullName = getFullName(teacher.first_name, teacher.middle_name, teacher.last_name);

    try {
      const wordHTML = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:w="urn:schemas-microsoft-com:office:word"
              xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8">
          <title>Teacher Details - ${fullName}</title>
          <!--[if gte mso 9]>
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
            </w:WordDocument>
          </xml>
          <![endif]-->
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Times New Roman', Times, serif;
              padding: 30px 40px;
              color: #1a1a1a;
              font-size: 12px;
              background: white;
            }
            .print-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 3px solid #1a3a5c;
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            .print-header-left h1 {
              font-size: 20px;
              color: #1a3a5c;
              font-weight: bold;
              letter-spacing: 1px;
            }
            .print-header-left p {
              color: #555;
              font-size: 11px;
              margin-top: 3px;
            }
            .print-header-right {
              text-align: right;
              font-size: 10px;
              color: #666;
            }
            .print-profile {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 25px;
              padding: 15px;
              background: #f8f9fa;
              border: 1px solid #e0e0e0;
              border-radius: 4px;
            }
            .print-profile-info h2 {
              font-size: 18px;
              color: #1a3a5c;
              margin-bottom: 5px;
            }
            .print-profile-info .sub-info {
              color: #555;
              font-size: 12px;
            }
            .print-profile-info .sub-info span {
              display: inline-block;
              margin-right: 15px;
            }
            .print-profile-image {
              width: 100px;
              height: 120px;
              border: 2px solid #1a3a5c;
              border-radius: 4px;
              overflow: hidden;
              flex-shrink: 0;
              background: #f0f0f0;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .print-profile-image img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
            .print-profile-image .initials {
              font-size: 32px;
              font-weight: bold;
              color: #1a3a5c;
            }
            .status-badge {
              display: inline-block;
              padding: 2px 10px;
              border-radius: 3px;
              font-size: 10px;
              font-weight: 600;
              text-transform: uppercase;
            }
            .status-active { background: #d4edda; color: #155724; }
            .status-onleave { background: #fff3cd; color: #856404; }
            .status-terminated { background: #f8d7da; color: #721c24; }
            .status-inactive { background: #e2e3e5; color: #383d41; }

            .print-section {
              margin-bottom: 20px;
            }
            .print-section h3 {
              font-size: 13px;
              color: #1a3a5c;
              border-bottom: 2px solid #e0e0e0;
              padding-bottom: 5px;
              margin-bottom: 10px;
              text-transform: uppercase;
              letter-spacing: 1px;
              font-weight: bold;
            }
            .print-table {
              width: 100%;
              border-collapse: collapse;
            }
            .print-table tr {
              border-bottom: 1px solid #f0f0f0;
            }
            .print-table td {
              padding: 6px 8px;
              font-size: 11px;
              vertical-align: top;
            }
            .print-table td:first-child {
              width: 35%;
              font-weight: 600;
              color: #444;
              background: #fafafa;
              padding-left: 12px;
            }
            .print-table td:last-child {
              width: 65%;
              color: #1a1a1a;
            }
            .print-footer {
              text-align: center;
              margin-top: 25px;
              padding-top: 12px;
              border-top: 1px solid #e0e0e0;
              color: #888;
              font-size: 9px;
            }
            .print-footer p { margin-top: 2px; }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="print-header-left">
              <h1>PAWASCHOOL</h1>
              <p>Teacher Information Report</p>
            </div>
            <div class="print-header-right">
              <p>Generated: ${new Date().toLocaleDateString()}</p>
              <p>Time: ${new Date().toLocaleTimeString()}</p>
            </div>
          </div>

          <div class="print-profile">
            <div class="print-profile-info">
              <h2>${fullName}</h2>
              <div class="sub-info">
                <span>Employee #: ${teacher.employee_number}</span>
                <span>|</span>
                <span>Status: ${getStatusLabel(teacher.status)}</span>
                <span>|</span>
                <span>${teacher.qualification || 'N/A'}</span>
              </div>
              <div style="margin-top: 6px;">
                <span class="status-badge ${teacher.status === 'active' ? 'status-active' : teacher.status === 'on_leave' ? 'status-onleave' : teacher.status === 'terminated' ? 'status-terminated' : 'status-inactive'}">${getStatusLabel(teacher.status).toUpperCase()}</span>
                <span style="margin-left: 10px; font-size: 11px; color: #555;">Joined: ${formatDate(teacher.joining_date)}</span>
              </div>
            </div>
            <div class="print-profile-image">
              ${teacher.profile_picture && !imageError ?
                `<img src="${imageUrl}" alt="${fullName}" style="width:100%;height:100%;object-fit:cover;" />` :
                `<div class="initials">${getInitials(teacher.first_name, teacher.last_name)}</div>`
              }
            </div>
          </div>

          <div class="print-section">
            <h3>Personal Information</h3>
            <table class="print-table">
              <tr><td>Full Name</td><td>${fullName}</td></tr>
              <tr><td>Employee Number</td><td>${teacher.employee_number}</td></tr>
              <tr><td>Date of Birth</td><td>${formatDate(teacher.date_of_birth)}</td></tr>
              <tr><td>Nationality</td><td>${teacher.nationality || 'Not set'}</td></tr>
              <tr><td>Address</td><td>${[teacher.address, teacher.city, teacher.state, teacher.country].filter(Boolean).join(', ') || 'Not set'}</td></tr>
              <tr><td>Phone</td><td>${teacher.phone || 'Not set'}</td></tr>
              <tr><td>Email</td><td>${teacher.email || 'Not set'}</td></tr>
            </table>
          </div>

          <div class="print-section">
            <h3>Professional Information</h3>
            <table class="print-table">
              <tr><td>Qualification</td><td>${teacher.qualification || 'N/A'}</td></tr>
              <tr><td>Teacher Status</td><td>${teacher.teacher_status || 'N/A'}</td></tr>
              <tr><td>PIN Code</td><td>${teacher.pin_code || 'N/A'}</td></tr>
              <tr><td>NIN Number</td><td>${teacher.nin_number || 'N/A'}</td></tr>
              <tr><td>Years of Experience</td><td>${teacher.years_of_experience || 0} years</td></tr>
              <tr><td>Joining Date</td><td>${formatDate(teacher.joining_date)}</td></tr>
              <tr><td>Current Class</td><td>${teacher.currentClass?.name || 'Not assigned'}</td></tr>
            </table>
          </div>

          <div class="print-section">
            <h3>Assigned Classes</h3>
            <div style="font-size: 11px; padding: 6px 8px;">
              ${teacher.classes && teacher.classes.length > 0 ?
                teacher.classes.map(cls =>
                  `${cls.name} - ${cls.level} (${cls.faculty})${cls.id === teacher.current_class_id ? ' (Current)' : ''}`
                ).join('<br>') :
                'None assigned'
              }
            </div>
          </div>

          <div class="print-section">
            <h3>Subject Assignments</h3>
            <div style="font-size: 11px; padding: 6px 8px;">
              ${assignedSubjects && assignedSubjects.length > 0 ?
                assignedSubjects.map(item => {
                  const assignment = resolveSubjectAssignment(item, subjectsList, classesList);
                  return `${assignment.subjectName} - ${assignment.className}`;
                }).join('<br>') :
                'None assigned'
              }
            </div>
          </div>

          ${statistics ? `
          <div class="print-section">
            <h3>Statistics</h3>
            <table class="print-table">
              <tr><td>Total Students</td><td>${statistics.total_students || 0}</td></tr>
              <tr><td>Classes Taught</td><td>${statistics.classes_taught?.length || 0}</td></tr>
              <tr><td>Current Class Students</td><td>${statistics.current_class_students || 0}</td></tr>
            </table>
          </div>
          ` : ''}

          <div class="print-footer">
            <p>This is a computer-generated document. No signature required.</p>
            <p>PawaSchool Management System | All Rights Reserved</p>
          </div>
        </body>
        </html>
      `;

      const blob = new Blob([wordHTML], { type: 'application/msword' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${teacher.first_name}_${teacher.last_name}_Teacher_Details.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      await showSuccess({ text: 'Word document downloaded successfully!' });
    } catch (error) {
      console.error('Export Word error:', error);
      await showError({ text: 'Failed to export Word document' });
    } finally {
      setIsExportingWord(false);
      setShowPrintMenu(false);
    }
  };

  if (loading) {
    return <FireLoader fullScreen={true} size="lg" text="LOADING..." />;
  }

  if (!teacher) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-primary-700">Teacher not found</h2>
        <button
          onClick={() => navigate('/app/teachers')}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors shadow-sm"
        >
          Back to Teachers
        </button>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(teacher.status);
  const showImage = imageUrl && !imageError;
  const fullName = getFullName(teacher.first_name, teacher.middle_name, teacher.last_name);
  const initials = getInitials(teacher.first_name, teacher.last_name);

  return (
    <div className="space-y-6 max-w-6xl mx-auto px-4 sm:px-0 animate-fade-in">
      {/* Header with back button and action buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <button
          onClick={() => navigate('/app/teachers')}
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Back to Teachers
        </button>
        <div className="flex gap-3 relative">
          {/* Print / Export Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowPrintMenu(!showPrintMenu)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
            >
              <PrinterIcon className="w-5 h-5" />
              Print / Export
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showPrintMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50">
                <button
                  onClick={handlePrint}
                  disabled={isPrinting}
                  className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <DocumentIcon className="w-5 h-5 mr-3 text-blue-600" />
                  <span>{isPrinting ? 'Preparing...' : 'Print as PDF'}</span>
                  <span className="ml-auto text-xs text-gray-400">Ctrl+P</span>
                </button>
                <button
                  onClick={handleExportWord}
                  disabled={isExportingWord}
                  className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 border-t border-gray-100"
                >
                  <DocumentArrowDownIcon className="w-5 h-5 mr-3 text-blue-600" />
                  <span>{isExportingWord ? 'Exporting...' : 'Export as Word'}</span>
                  <span className="ml-auto text-xs text-gray-400">.doc</span>
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => navigate(`/app/teachers/edit/${teacher.id}`)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors shadow-sm"
          >
            <PencilIcon className="w-5 h-5" />
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors shadow-sm"
          >
            <TrashIcon className="w-5 h-5" />
            Delete
          </button>
        </div>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-shrink-0 relative">
            <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center mx-auto md:mx-0 overflow-hidden">
              {showImage ? (
                <img
                  src={imageUrl}
                  alt={fullName}
                  className="w-full h-full rounded-full object-cover"
                  onError={() => setImageError(true)}
                />
              ) : (
                <span className="text-gray-700 font-bold text-4xl">
                  {initials}
                </span>
              )}
            </div>

            <label
              htmlFor="profile-upload"
              className={`absolute -bottom-1 -right-1 bg-primary-600 rounded-full p-1.5 shadow-lg cursor-pointer hover:bg-primary-700 transition-colors ${
                uploading ? 'opacity-70 pointer-events-none' : ''
              }`}
              title={isMobile ? "Take photo or select from gallery" : "Upload profile picture"}
            >
              <CameraIcon className="w-3.5 h-3.5 text-white" />
              <input
                id="profile-upload"
                type="file"
                accept="image/*"
                capture={isMobile ? "environment" : undefined}
                onChange={handleProfilePictureUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>

            {teacher.profile_picture && (
              <button
                onClick={handleDeleteProfilePicture}
                className="absolute -bottom-1 -left-1 bg-red-500 rounded-full p-1.5 shadow-lg cursor-pointer hover:bg-red-600 transition-colors"
                title="Remove profile picture"
              >
                <TrashIcon className="w-3.5 h-3.5 text-white" />
              </button>
            )}
          </div>

          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {fullName}
                </h1>
                <p className="text-gray-500">#{teacher.employee_number}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(teacher.status)}`}>
                    <StatusIcon className="w-3 h-3" />
                    {getStatusLabel(teacher.status)}
                  </span>
                  {teacher.teacher_status && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                      {teacher.teacher_status}
                    </span>
                  )}
                  {teacher.qualification && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                      {teacher.qualification}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Years of Experience</p>
                <p className="text-2xl font-bold text-gray-800">{teacher.years_of_experience || 0} years</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Information Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <UserIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Full Name</p>
                <p className="font-medium text-gray-800">{fullName}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <EnvelopeIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-800">{teacher.email || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <PhoneIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium text-gray-800">{teacher.phone || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CalendarIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Date of Birth</p>
                <p className="font-medium text-gray-800">{teacher.date_of_birth || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <GlobeAltIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Nationality</p>
                <p className="font-medium text-gray-800">{teacher.nationality || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPinIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Address</p>
                <p className="font-medium text-gray-800">{teacher.address || 'N/A'}</p>
                <p className="text-sm text-gray-500">
                  {teacher.city}, {teacher.state}, {teacher.country}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Professional Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Professional Information</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <AcademicCapIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Qualification</p>
                <p className="font-medium text-gray-800">{teacher.qualification || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <UserIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Teacher Status</p>
                <p className="font-medium text-gray-800">{teacher.teacher_status || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <IdentificationIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">PIN Code</p>
                <p className="font-medium text-gray-800">{teacher.pin_code || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <IdentificationIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">NIN Number</p>
                <p className="font-medium text-gray-800">{teacher.nin_number || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <BookOpenIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Class Taught</p>
                <p className="font-medium text-gray-800">{teacher.class_taught || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CalendarIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Joining Date</p>
                <p className="font-medium text-gray-800">{teacher.joining_date || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <BuildingOfficeIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Current Class</p>
                <p className="font-medium text-gray-800">
                  {teacher.currentClass?.name || 'Not assigned'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subject Assignments Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-gray-600" />
            Subject Assignments ({assignedSubjects.length})
          </h2>
          <button
            onClick={handleOpenAssignModal}
            className="inline-flex items-center gap-1 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm shadow-sm"
          >
            <PlusIcon className="w-4 h-4" />
            Assign Subject
          </button>
        </div>

        {fetchingAssignments ? (
          <div className="text-center py-4 text-gray-500">Loading assignments...</div>
        ) : assignedSubjects.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
            <p>No subjects assigned yet.</p>
            <p className="text-sm">Click "Assign Subject" to add one.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {assignedSubjects.map((item, index) => {
              const assignment = resolveSubjectAssignment(item, subjectsList, classesList);
              return (
              <div
                key={`${assignment.subjectId || 'subject'}-${assignment.classId || 'class'}-${index}`}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div>
                  <p className="font-medium text-gray-800">
                    {assignment.subjectName}
                  </p>
                  <p className="text-sm text-gray-500">
                    {assignment.className}
                  </p>
                </div>
                <button
                  onClick={() => handleRemoveAssignment(assignment.subjectId, assignment.classId)}
                  disabled={!assignment.subjectId || !assignment.classId}
                  className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                  title="Remove assignment"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Assigned Classes */}
      {(teacher.classes && teacher.classes.length > 0) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <BookOpenIcon className="w-5 h-5 text-gray-600" />
              Assigned Classes ({teacher.classes.length})
            </h2>
            <button
              onClick={() => navigate(`/app/teachers/edit/${teacher.id}`)}
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Manage Classes
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {teacher.classes.map((cls) => (
              <div
                key={cls.id}
                className={`flex items-center justify-between p-4 rounded-lg transition-colors cursor-pointer ${
                  cls.id === teacher.current_class_id
                    ? 'bg-primary-50 border-2 border-primary-200'
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
                onClick={() => navigate(`/app/classes/${cls.id}`)}
              >
                <div>
                  <p className="font-medium text-gray-800">{cls.name}</p>
                  <p className="text-sm text-gray-500 capitalize">{cls.level} - {cls.faculty}</p>
                  {cls.id === teacher.current_class_id && (
                    <span className="text-xs text-primary-600 font-medium">Current Class</span>
                  )}
                </div>
                <button className="text-primary-600 hover:text-primary-700 text-sm">
                  View →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Statistics */}
      {statistics && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Statistics</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-700">{statistics.total_students || 0}</p>
              <p className="text-sm text-gray-500">Total Students</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-700">{statistics.years_of_experience || 0}</p>
              <p className="text-sm text-gray-500">Years Experience</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-700">{statistics.classes_taught?.length || 0}</p>
              <p className="text-sm text-gray-500">Classes Taught</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-700">{statistics.current_class_students || 0}</p>
              <p className="text-sm text-gray-500">Current Class Students</p>
            </div>
          </div>
        </div>
      )}

      {/* ====== ASSIGN SUBJECT MODAL ====== */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-lg border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">Assign Subject</h3>
              <button
                onClick={() => setShowAssignModal(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject *
                </label>
                {loadingDropdown ? (
                  <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500">
                    Loading subjects...
                  </div>
                ) : (
                  <select
                    value={selectedSubjectId}
                    onChange={(e) => setSelectedSubjectId(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-gray-50/50 text-gray-700"
                  >
                    <option value="">-- Select Subject --</option>
                    {subjectsList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} {s.code ? `(${s.code})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Class *
                </label>
                {loadingDropdown ? (
                  <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500">
                    Loading classes...
                  </div>
                ) : (
                  <select
                    value={selectedClassId}
                    onChange={(e) => setSelectedClassId(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 bg-gray-50/50 text-gray-700"
                  >
                    <option value="">-- Select Class --</option>
                    {classesList.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.level ? `(${c.level})` : ''} {c.faculty ? `- ${c.faculty}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAssignModal(false)}
                className="flex-1 py-3 border border-gray-200 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignSubject}
                disabled={assigning || !selectedSubjectId || !selectedClassId || loadingDropdown}
                className="flex-1 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm"
              >
                {assigning ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Assigning...
                  </>
                ) : (
                  'Assign'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}